import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { CompanyUserRole, User, UserAccountingFirm } from "@/types/user";
import { type DocumentNode, gql, useMutation } from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import type { UserCompanyRoleInput, UserQueryData } from "./types";

type UpdateAccountingFirmUserInput = {
  userId: string;
  userData: {
    name?: string;
    email?: string;
    accountingFirmRole?: {
      accountingFirmId: string;
      role: string;
    };
    companyAccess?: UserCompanyRoleInput[];
  };
};

type UpdateCompanyUserInput = {
  userId: string;
  userData: {
    name?: string;
    email?: string;
    companyAccess?: UserCompanyRoleInput[];
  };
};

type RefetchEntry =
  | DocumentNode
  | { query: DocumentNode; variables?: Record<string, any> };

export const useUpdateUser = (refetchQueries: RefetchEntry[]) => {
  const { t } = useTranslation();

  const [updateUser, { data, loading, error }] = useMutation<UserQueryData>(
    UPDATE_ACCOUNTING_FIRM_USER_MUTATION,
    {
      refetchQueries: refetchQueries.map((entry) =>
        "query" in (entry as any)
          ? (entry as any)
          : { query: entry as DocumentNode },
      ),
    },
  );

  const handleUpdateAccountingFirmUser = async (
    data: UpdateAccountingFirmUserInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const response = await updateUser({
        variables: {
          userId: data.userId,
          userData: data.userData,
        },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return [handleUpdateAccountingFirmUser, { data, loading, error }] as const;
};

export const useUpdateCompanyUser = (refetchQueries: RefetchEntry[]) => {
  const { t } = useTranslation();

  const [updateUser, { data, loading, error }] = useMutation<UserQueryData>(
    UPDATE_COMPANY_USER_MUTATION,
    {
      refetchQueries: refetchQueries.map((entry) =>
        "query" in (entry as any)
          ? (entry as any)
          : { query: entry as DocumentNode },
      ),
    },
  );

  const handleUpdateCompanyUser = async (
    data: UpdateCompanyUserInput,
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));
    try {
      const response = await updateUser({
        variables: {
          userId: data.userId,
          userData: data.userData,
        },
      });
      toast.success(t("generic_success"), { id: toastId });
      onSuccess();
      return response;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
      throw error;
    }
  };

  return [handleUpdateCompanyUser, { data, loading, error }] as const;
};

export const UPDATE_ACCOUNTING_FIRM_USER_MUTATION = gql`
  mutation UpdateAccountingFirmUser($userId: String!, $userData: AccountingFirmUserInput!) {
    updateAccountingFirmUser(userId: $userId, userData: $userData) {
      userId
      email
      name
      systemRole
      accountingFirms {
        accountingFirmId
        accountingFirmName
        role
      }
      companies {
        companyId
        companyName
        role
      }
    }
  }
`;

export const UPDATE_COMPANY_USER_MUTATION = gql`
  mutation UpdateCompanyUser($userId: String!, $userData: CompanyUserInput!) {
    updateCompanyUser(userId: $userId, userData: $userData) {
      userId
      email
      name
      systemRole
      accountingFirms {
        accountingFirmId
        accountingFirmName
        role
      }
      companies {
        companyId
        companyName
        role
      }
    }
  }
`;
